import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import type { LandmarkDef, NpcDef, NpcId } from "./catalog";
import { PLANET_RADIUS, latLonToVector3, orientOnSphere, tangentBasis } from "./sphere";

export type TickFn = (time: number, moving: boolean) => void;

function mat(color: number, extras: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness: 0.62, metalness: 0.12, ...extras });
}

function labelEl(text: string, className: string): CSS2DObject {
  const el = document.createElement("div");
  el.className = className;
  el.textContent = text;
  const obj = new CSS2DObject(el);
  obj.position.set(0, 1.35, 0);
  return obj;
}

export function createLandmark(def: LandmarkDef): THREE.Group {
  const group = new THREE.Group();
  group.name = def.id;
  const figure = new THREE.Group();
  if (def.id === "workshop") {
    figure.add(makeWorkshop());
  } else if (def.id === "lighthouse") {
    figure.add(makeLighthouse());
  } else {
    figure.add(makeGarden());
  }
  group.add(figure);
  const tag = labelEl(def.name, "world-label");
  tag.position.set(0, 2.15, 0);
  group.add(tag);
  const pos = latLonToVector3(def.lat, def.lon, PLANET_RADIUS);
  orientOnSphere(group, pos, tangentBasis(pos).north);
  return group;
}

export function createNpc(def: NpcDef): THREE.Group {
  const group = new THREE.Group();
  group.name = def.id;
  const figure = makeNpcFigure(def.id);
  group.add(figure);
  const tag = labelEl(def.name, "npc-label");
  tag.position.set(0, 1.45, 0);
  group.add(tag);
  const pos = latLonToVector3(def.lat, def.lon, PLANET_RADIUS);
  orientOnSphere(group, pos, tangentBasis(pos).north);
  return group;
}

function makeWorkshop(): THREE.Group {
  const root = new THREE.Group();
  const hall = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.9, 1.15), mat(0x2a2436));
  hall.position.y = 0.45;
  const roof = new THREE.Mesh(new THREE.ConeGeometry(1.15, 0.55, 4), mat(0x8b7aa8));
  roof.position.y = 1.12;
  roof.rotation.y = Math.PI / 4;
  const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 0.55, 8), mat(0xc4a35a));
  stack.position.set(0.45, 1.35, -0.2);
  const bench = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.18, 0.4), mat(0x3a3228));
  bench.position.set(0, 0.18, 0.85);
  const lamp = new THREE.PointLight(0xe8c98a, 0.7, 7);
  lamp.position.set(0, 1.4, 0.2);
  root.add(hall, roof, stack, bench, lamp);
  return root;
}

function makeLighthouse(): THREE.Group {
  const root = new THREE.Group();
  const tower = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.32, 2.2, 10), mat(0xd8c7a2));
  tower.position.y = 1.1;
  const lamp = new THREE.Mesh(
    new THREE.BoxGeometry(0.42, 0.38, 0.42),
    mat(0xc4a35a, { emissive: 0x6a5220, emissiveIntensity: 0.8 }),
  );
  lamp.position.y = 2.28;
  const cap = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.28, 8), mat(0x2a2436));
  cap.position.y = 2.56;
  const light = new THREE.PointLight(0xffd79a, 1.1, 10);
  light.position.y = 2.3;
  const beam = new THREE.Mesh(
    new THREE.CylinderGeometry(0.04, 0.22, 3.2, 8, 1, true),
    new THREE.MeshBasicMaterial({ color: 0xc4a35a, transparent: true, opacity: 0.12, side: THREE.DoubleSide }),
  );
  beam.position.y = 3.6;
  addCandle(root, -0.7, 0.35, 0.7, true);
  addCandle(root, -0.5, 0.55, 0.42, false);
  addCandle(root, 0.65, 0.3, 0.86, true);
  addCandle(root, 0.85, 0.52, 0.34, false);
  root.add(tower, lamp, cap, light, beam);
  return root;
}

function addCandle(root: THREE.Group, x: number, z: number, h: number, up: boolean): void {
  const bar = new THREE.Mesh(new THREE.BoxGeometry(0.14, h, 0.14), mat(up ? 0x4f7a62 : 0x8a5348));
  bar.position.set(x, h / 2, z);
  root.add(bar);
}

function makeGarden(): THREE.Group {
  const root = new THREE.Group();
  const soil = new THREE.Mesh(new THREE.CylinderGeometry(1.15, 1.2, 0.12, 16), mat(0x243028));
  soil.position.y = 0.06;
  root.add(soil);
  const plants: Array<[number, number, number, number]> = [
    [-0.45, 0.35, 0.85, 0x6a8f7a],
    [0.1, -0.2, 1.15, 0x8b7aa8],
    [0.55, 0.4, 0.7, 0x4d6a78],
    [-0.15, 0.55, 0.55, 0xc4a35a],
  ];
  for (const [x, z, h, color] of plants) {
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.04, h, 6), mat(0x3d4f40));
    stem.position.set(x, h / 2, z);
    const orb = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.16, 0),
      mat(color, { emissive: color, emissiveIntensity: 0.18 }),
    );
    orb.position.set(x, h + 0.12, z);
    root.add(stem, orb);
  }
  const glow = new THREE.PointLight(0x8b7aa8, 0.45, 6);
  glow.position.set(0, 1.1, 0);
  root.add(glow);
  return root;
}

export function makeNpcFigure(id: NpcId): THREE.Group {
  switch (id) {
    case "flame":
      return makeFlame();
    case "robot":
      return makeRobot();
    case "drop":
      return makeDrop();
    case "butterfly":
      return makeButterfly();
    case "hedgehog":
      return makeHedgehog();
    case "balloon":
      return makeBalloon();
    case "fox":
      return makeFox();
    case "snail":
      return makeSnail();
  }
}

function makeFlame(): THREE.Group {
  const root = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.ConeGeometry(0.28, 0.85, 7),
    mat(0xa56b5c, { emissive: 0x5a2818, emissiveIntensity: 0.65 }),
  );
  body.position.y = 0.48;
  const core = new THREE.Mesh(
    new THREE.ConeGeometry(0.14, 0.5, 6),
    mat(0xc4a35a, { emissive: 0x8a6a20, emissiveIntensity: 0.8 }),
  );
  core.position.y = 0.42;
  const light = new THREE.PointLight(0xd08050, 0.55, 4);
  light.position.y = 0.6;
  root.add(body, core, light);
  root.userData.tick = (time: number) => {
    const s = 1 + Math.sin(time * 11) * 0.07;
    body.scale.set(s, 1 + Math.sin(time * 13) * 0.08, s);
  };
  return root;
}

function makeRobot(): THREE.Group {
  const root = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.55, 0.38), mat(0x4d6a78));
  body.position.y = 0.48;
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.26, 0.3), mat(0x2a2436));
  head.position.y = 0.88;
  const visor = new THREE.Mesh(
    new THREE.BoxGeometry(0.28, 0.08, 0.06),
    mat(0x6a8f7a, { emissive: 0x245a48, emissiveIntensity: 0.9 }),
  );
  visor.position.set(0, 0.9, 0.16);
  const legL = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.28, 0.12), mat(0x1c2428));
  const legR = legL.clone();
  legL.position.set(-0.12, 0.14, 0);
  legR.position.set(0.12, 0.14, 0);
  root.add(body, head, visor, legL, legR);
  root.userData.tick = (time: number, moving: boolean) => {
    visor.material = visor.material as THREE.MeshStandardMaterial;
    const pulse = 0.7 + Math.sin(time * 4) * 0.3;
    (visor.material as THREE.MeshStandardMaterial).emissiveIntensity = pulse;
    if (moving) {
      legL.rotation.x = Math.sin(time * 8) * 0.4;
      legR.rotation.x = -Math.sin(time * 8) * 0.4;
    }
  };
  return root;
}

function makeDrop(): THREE.Group {
  const root = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.SphereGeometry(0.28, 16, 12),
    mat(0x3d6a7a, { roughness: 0.18, metalness: 0.28 }),
  );
  body.position.y = 0.38;
  body.scale.set(0.85, 1.25, 0.85);
  const highlight = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 8), mat(0xd8e4e8, { roughness: 0.1 }));
  highlight.position.set(-0.08, 0.5, 0.16);
  root.add(body, highlight);
  root.userData.tick = (time: number) => {
    body.scale.y = 1.25 + Math.sin(time * 2.2) * 0.06;
  };
  return root;
}

function makeButterfly(): THREE.Group {
  const root = new THREE.Group();
  const thorax = new THREE.Mesh(new THREE.CapsuleGeometry(0.05, 0.28, 4, 8), mat(0x2a2436));
  thorax.position.y = 0.55;
  thorax.rotation.z = Math.PI / 2;
  const wingGeo = new THREE.PlaneGeometry(0.55, 0.38);
  const wingMat = new THREE.MeshStandardMaterial({
    color: 0x8b7aa8,
    side: THREE.DoubleSide,
    roughness: 0.45,
    metalness: 0.05,
  });
  const left = new THREE.Mesh(wingGeo, wingMat);
  const right = new THREE.Mesh(wingGeo, wingMat.clone());
  left.position.set(-0.22, 0.55, 0);
  right.position.set(0.22, 0.55, 0);
  root.add(thorax, left, right);
  root.userData.tick = (time: number) => {
    const flap = Math.sin(time * 9) * 0.55;
    left.rotation.y = -0.7 + flap;
    right.rotation.y = 0.7 - flap;
    root.position.y = Math.sin(time * 2.4) * 0.06;
  };
  return root;
}

function makeHedgehog(): THREE.Group {
  const root = new THREE.Group();
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.32, 14, 10), mat(0x6a5344));
  body.position.y = 0.32;
  body.scale.set(1.15, 0.8, 1);
  root.add(body);
  const spikeMat = mat(0x3a3228);
  for (let i = 0; i < 14; i += 1) {
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.22, 5), spikeMat);
    const a = (i / 14) * Math.PI * 2;
    spike.position.set(Math.cos(a) * 0.22, 0.48, Math.sin(a) * 0.18);
    spike.rotation.x = 0.5;
    root.add(spike);
  }
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 8), mat(0x2a2436));
  nose.position.set(0, 0.3, 0.34);
  root.add(nose);
  return root;
}

function makeBalloon(): THREE.Group {
  const root = new THREE.Group();
  const ball = new THREE.Mesh(
    new THREE.SphereGeometry(0.32, 16, 12),
    mat(0xa56b7c, { roughness: 0.35 }),
  );
  ball.position.y = 1.05;
  const knot = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 8), mat(0x2a2436));
  knot.position.y = 0.72;
  const stringGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0.72, 0),
    new THREE.Vector3(0, 0.18, 0),
  ]);
  const string = new THREE.Line(stringGeo, new THREE.LineBasicMaterial({ color: 0xc4a35a }));
  const basket = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.1, 0.16), mat(0x3a3228));
  basket.position.y = 0.12;
  root.add(ball, knot, string, basket);
  root.userData.tick = (time: number) => {
    ball.position.y = 1.05 + Math.sin(time * 1.6) * 0.08;
    knot.position.y = 0.72 + Math.sin(time * 1.6) * 0.08;
  };
  return root;
}

function makeFox(): THREE.Group {
  const root = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.18, 0.42, 4, 8), mat(0x8a4f32));
  body.position.y = 0.38;
  body.rotation.z = Math.PI / 2;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), mat(0x8a4f32));
  head.position.set(0.32, 0.48, 0);
  const snout = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.18, 6), mat(0xd8c7a2));
  snout.position.set(0.46, 0.44, 0);
  snout.rotation.z = -Math.PI / 2;
  const earL = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.16, 5), mat(0x2a2436));
  const earR = earL.clone();
  earL.position.set(0.28, 0.64, 0.08);
  earR.position.set(0.28, 0.64, -0.08);
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.34, 6), mat(0x6a3a24));
  tail.position.set(-0.36, 0.4, 0);
  tail.rotation.z = Math.PI / 2.6;
  root.add(body, head, snout, earL, earR, tail);
  root.userData.tick = (time: number, moving: boolean) => {
    tail.rotation.y = Math.sin(time * (moving ? 8 : 2)) * 0.35;
  };
  return root;
}

function makeSnail(): THREE.Group {
  const root = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.12, 0.42, 4, 8), mat(0x6a8f7a));
  body.position.y = 0.14;
  body.rotation.z = Math.PI / 2;
  const shell = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.1, 8, 16), mat(0x8a7344, { metalness: 0.2 }));
  shell.position.set(-0.05, 0.32, 0);
  shell.rotation.y = Math.PI / 2;
  const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 6), mat(0x2a2436));
  const eyeR = eyeL.clone();
  eyeL.position.set(0.28, 0.28, 0.06);
  eyeR.position.set(0.28, 0.28, -0.06);
  root.add(body, shell, eyeL, eyeR);
  root.userData.tick = (time: number) => {
    shell.rotation.z = Math.sin(time * 1.4) * 0.08;
  };
  return root;
}
